/**
 * The parts diagram: this product on its machine's exploded drawing.
 *
 * Data comes from the Fitment API (assets/fitment-api.js, window.Fitment),
 * reached through the app proxy /apps/fitment. Three calls:
 *
 *   check(brand, model, sku)     is the part on a drawing for this machine?
 *                                → the "View on the parts diagram" button
 *   fitment(brand, model, sku)   the assemblies it is listed on (with `via`
 *                                when reached through a number it replaced)
 *   diagram(brand, ipl_key, sku) the drawing, its hotspots, every part on it
 *                                and which callouts are this part
 *
 * The machine is the garage's active vehicle, which the theme knows by its
 * Shopify key ("2025-polaris-sportsman-touring-570-eps"); the API knows it
 * by its own model key. The API's search matches a contiguous phrase of the
 * name, so the model words with the year are tried first, then the words
 * alone narrowed to the year, then the year's folder in the brand's tree,
 * where the words may sit in another order (the catalog's "SPORTSMAN
 * TOURING 570 X2 EPS" is the store's "Sportsman X2 570 EPS"); the match is
 * remembered per machine in localStorage, and a select in the dialog lets
 * the customer correct it when several trims matched.
 *
 * The SKU is Shopify's with its "12POL_" prefix removed (Lucas, 2026-10-10);
 * the brand code comes from the product's vendor.
 *
 * The parts list beside the drawing (Lucas's three states): a part not in
 * the store shows its number and description alone; one in the store but
 * out of stock shows its price and "Out of stock"; one in stock shows its
 * price and an Add button. "In the store" is the machine's own collection
 * (collection-feed.js), matched by SKU.
 *
 * Two layers (request 193, 2026-10-26): DiagramView is the drawing itself —
 * the stage with its hotspots and zoom, and the parts table with Lucas's
 * three states — inside whatever element holds that markup; PartDiagram is
 * the product page's dialog around it. The model viewer page
 * (model-viewer.js) puts a DiagramView in the page for any assembly of the
 * chosen machine, and uses the machine match (machineFor, chooseMachine)
 * from here, so the two never disagree about which model a machine is.
 */
(function () {
  'use strict';

  const BRAND_CODES = { polaris: 'POL' };
  const SKU_PREFIX = /^12[A-Z0-9]{3}_/i; // "12POL_2889050" → "2889050"
  const CACHE_KEY = 'fitment.models';
  const MAX_CANDIDATES = 8;
  const ZOOM_STEP = 0.5; // the drawing's zoom buttons (request 161)
  const ZOOM_MAX = 4;

  const money = (amount) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD' }).format(n);
    } catch { return `$${n.toFixed(2)}`; }
  };
  const bare = (sku) => String(sku || '').trim().replace(SKU_PREFIX, '');
  const brandOf = (vendor) => BRAND_CODES[String(vendor || '').trim().toLowerCase()] || null;
  const upper = (s) => String(s || '').toUpperCase().replace(/\s+/g, ' ').trim();
  /** "SPORTSMAN TOURING 570 EPS (2025) (R06) (A25SDA57A7/E57A4)" → "SPORTSMAN TOURING 570 EPS". */
  const stem = (name) => upper(name).split(' (')[0];
  /** CSS.escape where the browser has it (jsdom does not); callouts are plain anyway. */
  const cssEscape = (value) => (window.CSS && typeof window.CSS.escape === 'function' ? window.CSS.escape(String(value)) : String(value).replace(/["\\]/g, '\\$&'));
  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  /* ---- Which API model is the garage's machine ---- */

  function modelWords(vehicle) {
    if (vehicle.model) return String(vehicle.model).trim();
    let label = String(vehicle.label || '').trim();
    const year = vehicle.year || (label.match(/^(\d{4})\s/) || [])[1];
    if (year) label = label.replace(new RegExp(`^${year}\\s+`), '');
    if (vehicle.make) label = label.replace(new RegExp(`^${vehicle.make}\\s+`, 'i'), '');
    else label = label.replace(/^[A-Za-z-]+\s+/, '');
    return label.trim();
  }

  function yearOf(vehicle) {
    return String(vehicle.year || (String(vehicle.key || '').match(/^(\d{4})-/) || [])[1] || '');
  }

  function readCache() {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}') || {}; } catch { return {}; }
  }

  function writeCache(map) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(map)); } catch { /* private mode */ }
  }

  /** The remembered match for a machine, or null. */
  function cached(vehicleKey) {
    return readCache()[vehicleKey] || null;
  }

  /** Add to a machine's remembered match (the model viewer keeps its drawing count there), or start one. */
  function remember(vehicleKey, patch) {
    if (!vehicleKey) return null;
    const cache = readCache();
    cache[vehicleKey] = Object.assign(cache[vehicleKey] || {}, patch);
    writeCache(cache);
    return cache[vehicleKey];
  }

  const wordsOf = (text) => upper(text).split(' ').filter(Boolean);

  /**
   * The models whose name holds the machine's words — as a phrase, or in any
   * order: the catalog says "SPORTSMAN TOURING 570 X2 EPS" where the store
   * says "Sportsman X2 570 EPS", and a phrase search finds nothing.
   */
  function related(models, words) {
    const target = upper(words);
    const need = wordsOf(target);
    return models.filter((m) => {
      const s = stem(m.name);
      if (s.includes(target) || upper(m.name).includes(target)) return true;
      const have = new Set(wordsOf(s));
      return need.length > 0 && need.every((w) => have.has(w));
    });
  }

  /** Best first: the year's, domestic before INTL, the exact name, then the closest — the phrase, then the words in another order with the fewest extra. */
  function rank(models, words, year) {
    const target = upper(words);
    const need = wordsOf(target);
    let list = models.filter((m) => !year || String(m.name).includes(`(${year})`));
    const domestic = list.filter((m) => !/^INTL\b/i.test(m.name));
    if (domestic.length) list = domestic;
    const score = (m) => {
      const s = stem(m.name);
      if (s === target) return 0;
      if (s.startsWith(target)) return 1 + (s.length - target.length) / 100;
      if (s.includes(target)) return 2;
      const have = wordsOf(s);
      if (need.length && need.every((w) => have.includes(w))) return 3 + (have.length - need.length) / 100;
      return 4;
    };
    return list.slice().sort((a, b) => score(a) - score(b) || String(a.name).length - String(b.name).length);
  }

  async function walkTree(brand, vehicle, words, year) {
    const api = window.Fitment;
    const roots = (await api.tree(brand)).folders || [];
    const type = upper(vehicle.type || '');
    const ordered = roots.slice().sort((a, b) => (upper(b.name) === type) - (upper(a.name) === type));
    const target = upper(words);
    // The root named like the machine's type first; a few more in case the
    // API files it elsewhere (RANGER, RZR and GENERAL are roots of their own).
    for (const root of ordered.slice(0, 6)) {
      const branch = await api.tree(brand, root.folder_key);
      const folder = (branch.folders || []).find((f) => String(f.name).startsWith(`${year} `) || String(f.name) === year);
      if (!folder) continue;
      const leaf = await api.tree(brand, folder.folder_key);
      const hits = related(leaf.models || [], target);
      if (hits.length) return hits;
    }
    return [];
  }

  async function candidates(brand, vehicle) {
    const api = window.Fitment;
    const words = modelWords(vehicle);
    const year = yearOf(vehicle);
    if (!words) return [];
    let found = [];
    try { found = (await api.resolve(brand, `${words} ${year}`.trim())).models || []; } catch { found = []; }
    if (!found.length) {
      try { found = ((await api.resolve(brand, words)).models || []).filter((m) => !year || String(m.name).includes(`(${year})`)); } catch { found = []; }
    }
    if (!found.length && year) {
      try { found = await walkTree(brand, vehicle, words, year); } catch { found = []; }
    }
    return rank(found, words, year);
  }

  async function machineFor(brand, vehicle) {
    const cache = readCache();
    const hit = cache[vehicle.key];
    if (hit && hit.brand === brand && hit.model_key) return hit;
    const list = await candidates(brand, vehicle);
    if (!list.length) return null;
    const entry = {
      brand,
      model_key: list[0].model_key,
      name: list[0].name,
      candidates: list.slice(0, MAX_CANDIDATES).map((m) => ({ model_key: m.model_key, name: m.name })),
    };
    cache[vehicle.key] = entry;
    writeCache(cache);
    return entry;
  }

  /** The customer corrected the match: remember it for this machine. */
  function chooseMachine(vehicleKey, modelKey) {
    const cache = readCache();
    const entry = cache[vehicleKey];
    if (!entry) return null;
    const pick = (entry.candidates || []).find((c) => c.model_key === modelKey);
    if (!pick) return entry;
    entry.model_key = pick.model_key;
    entry.name = pick.name;
    // A different model has its own drawings; the count is found again.
    delete entry.diagrams;
    writeCache(cache);
    return entry;
  }

  /* ---- The drawing: a stage with hotspots and zoom, and the parts table ---- */

  /**
   * Everything inside the element given as `root`: [data-diagram-figure] with
   * its scroller, stage, status line and zoom controls, and the parts table
   * [data-diagram-table]. The dialog and the model viewer page both hold that
   * markup. A host answers the two selects through onAssemblyChange and
   * onModelChange.
   */
  class DiagramView {
    constructor(root) {
      this.root = root;
      this.loadToken = 0;
      this.zoom = 1;
      if (!root) return;

      root.addEventListener('change', (event) => {
        if (event.target.matches('[data-diagram-assembly-select]')) this.onAssemblyChange(event.target.value);
        else if (event.target.matches('[data-diagram-model-select]')) this.onModelChange(event.target.value);
      });
      root.addEventListener('click', (event) => {
        const hotspot = event.target.closest('[data-diagram-hotspot]');
        if (hotspot) { this.focusCallout(hotspot.dataset.callout, true); return; }
        const add = event.target.closest('[data-diagram-add]');
        if (add) { this.addToCart(add); return; }
        const zoom = event.target.closest('[data-diagram-zoom]');
        if (zoom) { this.setZoom((this.zoom || 1) + (zoom.dataset.diagramZoom === 'in' ? ZOOM_STEP : -ZOOM_STEP)); return; }
        const row = event.target.closest('[data-diagram-row]');
        if (row && !event.target.closest('a')) this.focusCallout(row.dataset.callout, false);
      });
      root.addEventListener('mouseover', (event) => {
        const row = event.target.closest('[data-diagram-row]');
        if (row) this.hoverCallout(row.dataset.callout);
      });
      root.addEventListener('mouseout', (event) => {
        if (event.target.closest('[data-diagram-row]')) this.hoverCallout(null);
      });
      const figure = root.querySelector('[data-diagram-figure]');
      if (figure && window.ResizeObserver) new ResizeObserver(() => this.fitStage()).observe(figure);
    }

    onAssemblyChange() {}

    onModelChange() {}

    q(selector) { return this.root ? this.root.querySelector(selector) : null; }

    status(text) {
      const node = this.q('[data-diagram-status]');
      if (node) { node.textContent = text || ''; node.hidden = !text; }
    }

    note(text) {
      const node = this.q('[data-diagram-note]');
      if (node) { node.textContent = text || ''; node.hidden = !text; }
    }

    /** Back to nothing shown: before a load, and for a machine with no drawing. */
    clear() {
      const stage = this.q('[data-diagram-stage]');
      if (stage) stage.hidden = true;
      const zoomControls = this.q('[data-diagram-zoom-controls]');
      if (zoomControls) zoomControls.hidden = true;
      const table = this.q('[data-diagram-table]');
      if (table) table.hidden = true;
      this.note('');
    }

    paintModelPicker(machine) {
      const wrap = this.q('[data-diagram-models]');
      const select = this.q('[data-diagram-model-select]');
      if (!wrap || !select) return;
      const list = (machine && machine.candidates) || [];
      wrap.hidden = list.length < 2;
      select.replaceChildren(...list.map((c) => {
        const option = el('option', '', c.name);
        option.value = c.model_key;
        option.selected = c.model_key === machine.model_key;
        return option;
      }));
    }

    paintAssemblyPicker(assemblies) {
      const wrap = this.q('[data-diagram-assemblies]');
      const select = this.q('[data-diagram-assembly-select]');
      if (!wrap || !select) return;
      wrap.hidden = assemblies.length < 2;
      select.replaceChildren(...assemblies.map((a) => {
        const option = el('option', '', `${a.title} — callout ${a.callout}${a.via ? ` (as ${a.via})` : ''}`);
        option.value = a.ipl_key;
        return option;
      }));
    }

    /** Products of the machine's collection, by bare SKU. */
    async storeMap(vehicleKey) {
      const map = new Map();
      if (!vehicleKey || !window.CollectionFeed) return map;
      try {
        const products = await window.CollectionFeed.load(vehicleKey);
        for (const p of products) {
          for (const v of p.variants || []) {
            const key = bare(v.sku);
            if (key && !map.has(key)) map.set(key, { handle: p.handle, title: p.title, variantId: v.id, price: v.price, available: !!v.available });
          }
        }
      } catch (err) {
        console.error('[part-diagram] feed', err);
      }
      return map;
    }

    paintStage(data) {
      const stage = this.q('[data-diagram-stage]');
      if (!stage) return;
      const width = Number(data.published_width || data.width) || 1;
      const height = Number(data.published_height || data.height) || 1;
      stage.dataset.width = String(width);
      stage.dataset.height = String(height);
      const mine = new Set((data.highlight || []).map((h) => String(h.callout)));

      const image = el('img', 'part-diagram__image');
      image.src = data.image_url || data.cdn_path || '';
      image.alt = data.title ? `Diagram: ${data.title}` : 'Parts diagram';
      image.width = width;
      image.height = height;
      image.decoding = 'async';

      const layer = el('div', 'part-diagram__hotspots');
      layer.setAttribute('data-diagram-hotspots', '');
      for (const spot of data.hotspots || []) {
        const callout = String(spot.callout);
        const x1 = Number(spot.x1), y1 = Number(spot.y1), x2 = Number(spot.x2), y2 = Number(spot.y2);
        if (![x1, y1, x2, y2].every(Number.isFinite)) continue;
        const button = el('button', `part-diagram__hotspot${mine.has(callout) ? ' part-diagram__hotspot--mine' : ''}`);
        button.type = 'button';
        button.setAttribute('data-diagram-hotspot', '');
        button.dataset.callout = callout;
        button.setAttribute('aria-label', `Callout ${callout}`);
        button.title = `Callout ${callout}`;
        button.style.left = `${(x1 / width) * 100}%`;
        button.style.top = `${(y1 / height) * 100}%`;
        button.style.width = `${((x2 - x1) / width) * 100}%`;
        button.style.height = `${((y2 - y1) / height) * 100}%`;
        layer.append(button);
      }
      stage.replaceChildren(image, layer);
      stage.hidden = false;
      const controls = this.q('[data-diagram-zoom-controls]');
      if (controls) controls.hidden = false;
      this.setZoom(1);
    }

    /**
     * The drawing's zoom (request 161): 1× fits the box; each step enlarges
     * the stage, which then pans inside the scroller. The hotspots are placed
     * in percentages, so they ride along.
     */
    setZoom(level) {
      const next = Math.min(ZOOM_MAX, Math.max(1, Math.round(level * 100) / 100));
      this.zoom = next;
      const out = this.q('[data-diagram-zoom-level]');
      if (out) out.textContent = `${next}×`;
      const less = this.q('[data-diagram-zoom="out"]');
      const more = this.q('[data-diagram-zoom="in"]');
      if (less) less.disabled = next <= 1;
      if (more) more.disabled = next >= ZOOM_MAX;
      this.fitStage();
    }

    /**
     * The stage is exactly the drawing's box inside the scroller (times the
     * zoom), so hotspots in % stay on their parts. On a phone the figure is in
     * the page's flow (--part-diagram-flow, see assets/part-diagram.css): the
     * stage takes the width and whatever height that needs.
     */
    fitStage() {
      const stage = this.q('[data-diagram-stage]');
      const figure = this.q('[data-diagram-figure]');
      const box = this.q('[data-diagram-scroller]') || figure;
      if (!stage || !figure || !box || stage.hidden) return;
      const width = Number(stage.dataset.width) || 1;
      const height = Number(stage.dataset.height) || 1;
      const styles = window.getComputedStyle(box);
      const inFlow = window.getComputedStyle(figure).getPropertyValue('--part-diagram-flow').trim() === '1';
      const boxW = box.clientWidth - (parseFloat(styles.paddingLeft) || 0) - (parseFloat(styles.paddingRight) || 0);
      const boxH = box.clientHeight - (parseFloat(styles.paddingTop) || 0) - (parseFloat(styles.paddingBottom) || 0);
      if (boxW <= 0 || (!inFlow && boxH <= 0)) return;
      const fit = inFlow ? boxW / width : Math.min(boxW / width, boxH / height);
      const scale = fit * (this.zoom || 1);
      stage.style.width = `${Math.floor(width * scale)}px`;
      stage.style.height = `${Math.floor(height * scale)}px`;
    }

    paintRows(data, store, sku) {
      const table = this.q('[data-diagram-table]');
      const body = this.q('[data-diagram-rows]');
      if (!table || !body) return;
      const mine = new Set((data.highlight || []).map((h) => String(h.callout)));
      const rows = (data.parts || []).map((part) => {
        const callout = String(part.callout || '');
        const number = String(part.part_number || '');
        const entry = store.get(number) || null;
        const tr = el('tr', `part-diagram__row${mine.has(callout) ? ' part-diagram__row--mine' : ''}`);
        tr.setAttribute('data-diagram-row', '');
        tr.dataset.callout = callout;
        tr.dataset.part = number;
        if (entry) tr.dataset.handle = entry.handle;

        tr.append(el('td', 'part-diagram__num', callout));

        const partCell = el('td', 'part-diagram__part');
        if (entry) {
          const link = el('a', 'part-diagram__sku', number);
          link.href = `/products/${entry.handle}`;
          partCell.append(link);
        } else {
          partCell.append(el('span', 'part-diagram__sku', number));
        }
        if (mine.has(callout)) partCell.append(el('span', 'part-diagram__mine-tag', 'This part'));
        partCell.append(el('span', 'part-diagram__desc', String(part.description || '')));
        if (part.superseded_by) partCell.append(el('span', 'part-diagram__replaced', `Replaced by ${part.superseded_by}`));
        for (const note of part.notes || []) partCell.append(el('span', 'part-diagram__replaced', String(note)));
        tr.append(partCell);

        tr.append(el('td', 'part-diagram__qty', String(part.quantity || '')));

        // Lucas's three states: not in the store — number and description
        // alone; in the store but out of stock — price and "Out of stock";
        // in stock — price and Add.
        const priceCell = el('td', 'part-diagram__price');
        const actionCell = el('td', 'part-diagram__action');
        if (!entry) {
          priceCell.textContent = '';
          tr.setAttribute('data-stock', 'none');
        } else {
          priceCell.textContent = money(entry.price);
          if (entry.available) {
            const add = el('button', 'part-diagram__add', 'Add');
            add.type = 'button';
            add.setAttribute('data-diagram-add', '');
            add.dataset.variantId = String(entry.variantId);
            // Add what the drawing calls for — the row's quantity (#112).
            const qty = Math.max(1, parseInt(part.quantity, 10) || 1);
            add.dataset.quantity = String(qty);
            add.setAttribute('aria-label', qty > 1 ? `Add ${qty} of ${number} to cart` : `Add ${number} to cart`);
            if (qty > 1) add.title = `Adds ${qty}, as the diagram calls for`;
            actionCell.append(add);
            tr.setAttribute('data-stock', 'in');
          } else {
            actionCell.append(el('span', 'part-diagram__stock', 'Out of stock'));
            tr.setAttribute('data-stock', 'out');
          }
        }
        tr.append(priceCell, actionCell);
        return tr;
      });
      body.replaceChildren(...rows);
      table.hidden = false;
    }

    async addToCart(button) {
      const id = Number(button.dataset.variantId);
      const quantity = Math.max(1, parseInt(button.dataset.quantity, 10) || 1);
      if (!id || !window.Cart || button.disabled) return;
      const original = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        await window.Cart.add([{ id, quantity }]);
        button.textContent = 'Added ✓';
        setTimeout(() => { button.textContent = original; button.disabled = false; }, 1800);
      } catch (err) {
        console.error('[part-diagram] add', err);
        button.textContent = original;
        button.disabled = false;
      }
    }

    /** A callout chosen on the drawing or in the list: both sides mark it, the list scrolls to it. */
    focusCallout(callout, fromDrawing) {
      if (!this.root || !callout) return;
      for (const node of this.root.querySelectorAll('[data-diagram-row], [data-diagram-hotspot]')) {
        node.classList.toggle('is-focused', node.dataset.callout === String(callout));
      }
      const row = this.root.querySelector(`[data-diagram-row][data-callout="${cssEscape(callout)}"]`);
      if (row && fromDrawing) row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      if (!fromDrawing) {
        const spot = this.root.querySelector(`[data-diagram-hotspot][data-callout="${cssEscape(callout)}"]`);
        if (spot) spot.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    hoverCallout(callout) {
      if (!this.root) return;
      for (const node of this.root.querySelectorAll('[data-diagram-hotspot]')) {
        node.classList.toggle('is-hover', !!callout && node.dataset.callout === String(callout));
      }
    }
  }

  /* ---- The product page's dialog ---- */

  class PartDiagram extends DiagramView {
    constructor(section) {
      super(section.querySelector('[data-part-diagram]'));
      this.section = section;
      this.dialog = this.root;
      this.button = section.querySelector('[data-part-diagram-open]');
      this.fitment = section.querySelector('product-fitment');
      this.token = 0;
      this.assemblies = [];
      this.current = null;

      document.addEventListener('garage:changed', () => this.refresh());
      // The variant decides the SKU (product-form.js keeps [data-mpn] in step).
      document.addEventListener('change', (event) => {
        if (event.target.closest('[data-variant-select]')) setTimeout(() => this.refresh(), 0);
      });

      this.refresh();
    }

    onAssemblyChange(iplKey) {
      this.showAssembly(iplKey);
    }

    onModelChange(modelKey) {
      const vehicle = window.Garage?.active();
      if (vehicle) chooseMachine(vehicle.key, modelKey);
      this.load();
      this.refresh();
    }

    /** The product's brand code and bare SKU, and the garage's machine. */
    context() {
      const vendor = this.fitment?.dataset.vendor || this.section.querySelector('[data-product-brand]')?.textContent || '';
      const skuText = this.section.querySelector('[data-mpn]')?.textContent || this.fitment?.dataset.sku || '';
      return { brand: brandOf(vendor), sku: bare(skuText), vehicle: window.Garage?.active() || null };
    }

    /** The button shows once the API says the part is on a drawing for this machine. */
    async refresh() {
      // A product without fitment (apparel, riding gear) is on no drawing.
      if (this.fitment && this.fitment.hasAttribute('data-no-fitment')) return;
      if (!this.button) return;
      const { brand, sku, vehicle } = this.context();
      const token = ++this.token;
      if (!brand || !sku || !vehicle || !window.Fitment) { this.button.hidden = true; return; }
      try {
        const machine = await machineFor(brand, vehicle);
        if (token !== this.token) return;
        if (!machine) { this.button.hidden = true; return; }
        const check = await window.Fitment.check(brand, machine.model_key, sku);
        if (token !== this.token) return;
        const on = !!(check && check.found && Number(check.with_drawing) > 0);
        this.button.hidden = !on;
        if (on) {
          // One wording; the dialog's Assembly picker says how many drawings.
          const n = Number(check.assemblies) || 0;
          this.button.textContent = 'View parts diagram';
          this.button.title = n > 1 ? `Shown on ${n} diagrams` : '';
        }
      } catch (err) {
        console.error('[part-diagram]', err);
        this.button.hidden = true;
      }
    }

    open() {
      if (!this.dialog) return;
      if (!this.dialog.open) this.dialog.showModal();
      this.load();
    }

    async load() {
      const { brand, sku, vehicle } = this.context();
      const token = ++this.loadToken;
      const title = this.q('[data-diagram-title]');
      const machineLine = this.q('[data-diagram-machine]');
      this.clear();
      if (title) title.textContent = 'Parts diagram';
      if (machineLine) machineLine.textContent = vehicle ? vehicle.nickname || vehicle.label : '';
      this.paintModelPicker(null);
      this.paintAssemblyPicker([]);

      if (!window.Fitment) { this.status('The diagram service is not available right now.'); return; }
      if (!vehicle) { this.status('Choose your machine to see this part on its diagram.'); return; }
      if (!brand) { this.status('Diagrams are not available for this brand yet.'); return; }
      if (!sku) { this.status('This product has no part number to look up.'); return; }
      this.status('Loading the diagram…');

      let machine;
      let fit;
      try {
        machine = await machineFor(brand, vehicle);
        if (token !== this.loadToken) return;
        if (!machine) { this.status(`We couldn't match your ${vehicle.label} to a diagram model yet.`); return; }
        this.paintModelPicker(machine);
        if (machineLine) machineLine.textContent = machine.name;
        fit = await window.Fitment.fitment(brand, machine.model_key, sku);
        if (token !== this.loadToken) return;
      } catch (err) {
        console.error('[part-diagram]', err);
        this.status('The diagram service did not answer. Try again in a moment.');
        return;
      }

      // Direct listings before ones reached through a replaced number.
      const assemblies = ((fit && fit.assemblies) || []).slice().sort((a, b) => (a.via ? 1 : 0) - (b.via ? 1 : 0));
      this.assemblies = assemblies;
      this.fit = fit;
      if (!assemblies.length) {
        this.status(`This part is not on a diagram for your ${vehicle.label}.`);
        return;
      }
      this.paintAssemblyPicker(assemblies);
      await this.showAssembly(assemblies[0].ipl_key);
    }

    async showAssembly(iplKey) {
      const { brand, sku, vehicle } = this.context();
      const assembly = this.assemblies.find((a) => a.ipl_key === iplKey) || this.assemblies[0];
      if (!assembly) return;
      const token = ++this.loadToken;
      const select = this.q('[data-diagram-assembly-select]');
      if (select) select.value = assembly.ipl_key;
      this.status('Loading the drawing…');
      let data;
      let store;
      try {
        [data, store] = await Promise.all([
          window.Fitment.diagram(brand, assembly.ipl_key, sku),
          this.storeMap(vehicle ? vehicle.key : null),
        ]);
        if (token !== this.loadToken) return;
      } catch (err) {
        console.error('[part-diagram]', err);
        this.status('The drawing did not load. Try again in a moment.');
        return;
      }
      this.current = { assembly, data, store, sku };
      this.status('');
      const title = this.q('[data-diagram-title]');
      if (title) title.textContent = data.title || assembly.title || 'Parts diagram';
      this.note(assembly.via ? `Listed here as ${assembly.via}, a number this part replaces.` : '');
      this.paintStage(data);
      this.paintRows(data, store, sku);
      const mine = (data.highlight || []).map((h) => String(h.callout));
      if (mine.length) this.focusCallout(mine[0], false);
    }
  }

  /* ---- Wiring ---- */

  const controllers = new WeakMap();
  function controllerFor(section) {
    if (!section) return null;
    if (!controllers.has(section)) controllers.set(section, new PartDiagram(section));
    return controllers.get(section);
  }
  function sectionOf(node) {
    return node.closest('.shopify-section') || document.querySelector('[data-part-diagram]')?.closest('.shopify-section') || document.body;
  }

  function boot() {
    for (const dialog of document.querySelectorAll('[data-part-diagram]')) controllerFor(sectionOf(dialog));
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-part-diagram-open]');
    if (opener) {
      event.preventDefault();
      controllerFor(sectionOf(opener))?.open();
      return;
    }
    const closer = event.target.closest('[data-part-diagram-close]');
    if (closer) {
      sectionOf(closer).querySelector('[data-part-diagram]')?.close();
      return;
    }
    // Clicking the backdrop: the dialog element's own box covers only the
    // panel, so a click landing on the dialog itself is outside the content.
    const dialog = event.target.closest('[data-part-diagram]');
    if (dialog?.open && event.target === dialog) dialog.close();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();

  window.PartDiagram = { boot, bare, brandOf, modelWords, rank, related, stem, machineFor, chooseMachine, cached, remember, DiagramView };
})();
